'use client';

import { useState } from 'react';
import { usePathname } from 'next/navigation';
import { trackEvent } from '@/lib/analytics';
import WorkCard from './WorkCard';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';

interface WorkCardData {
  href: string;
  imageSrc: string;
  imageAlt: string;
  title: string;
  linkText: string;
}

interface WorkCardsProps {
  cards: WorkCardData[];
  onGetInTouch?: () => void;
}

export default function WorkCards({ cards, onGetInTouch }: WorkCardsProps) {
  const pathname = usePathname();
  const [isHovered, setIsHovered] = useState(false);
  
  const handleGetInTouch = () => {
    trackEvent('ai_click', {
      click_text: 'Get in touch - start',
      page_path: pathname
    });
    onGetInTouch?.();
  };
  
  return (
    <div className="work-links w-full pl-[1.47rem] lg:pl-[1.35rem] pr-2 lg:pr-0 overflow-hidden">
      <p className="body-sm mb-4">I chose some of our work so you can dive deeper into these subjects...</p>

      {/* Mobile: Swiper Carousel (up to 1024px) */}
      <div className="work-card-container block lg:hidden">
        <Swiper
          modules={[Mousewheel]}
          spaceBetween={16} // 1rem
          slidesPerView="auto"
          mousewheel={{
            forceToAxis: true,
            sensitivity: 1,
            releaseOnEdges: true,
          }}
          className="work-cards-swiper"
        >
          {cards.map((card, index) => (
            <SwiperSlide key={index} className="work-card-slide">
              <WorkCard
                href={card.href}
                imageSrc={card.imageSrc}
                imageAlt={card.imageAlt}
                title={card.title}
                linkText={card.linkText}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Desktop: Flex Layout (1024px and above) */}
      <div className="work-card-container hidden lg:flex lg:flex-row gap-4">
        {cards.map((card, index) => (
          <WorkCard
            key={index}
            href={card.href}
            imageSrc={card.imageSrc}
            imageAlt={card.imageAlt}
            title={card.title}
            linkText={card.linkText}
          />
        ))}
      </div>

      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 pb-6 pt-4">
        <p className="body-sm flex-1">We&rsquo;d love to add a human touch to better understand your challenges. Reach out and let&rsquo;s talk.</p>
        <button 
          onClick={handleGetInTouch}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className="body-sm bg-red-600 hover:bg-red-700 text-white rounded-full px-6 py-2 whitespace-nowrap flex-shrink-0 transition-all duration-300 ease-out mr-4 lg:mr-0"
        >
          <div className="relative overflow-hidden px-2" style={{ willChange: 'transform' }}>
            <span 
              className={`block transition-transform duration-300 ease-out ${
                isHovered ? '-translate-y-full' : 'translate-y-0'
              }`}
              style={{ willChange: 'transform' }}
            >
              Get in touch
            </span>
            <span 
              className={`absolute inset-0 flex items-center justify-center transition-transform duration-300 ease-out ${
                isHovered ? 'translate-y-0' : 'translate-y-full'
              }`}
              style={{ willChange: 'transform' }}
            >
              Get in touch
            </span>
          </div>
        </button>
      </div>
    </div>
  );
}

// Add global styles for the swiper
if (typeof window !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    .work-cards-swiper {
      width: 100%;
    }

    .work-cards-swiper .swiper-slide {
      width: auto !important;
    }
  `;
  if (!document.head.querySelector('[data-workcards-swiper]')) {
    style.setAttribute('data-workcards-swiper', '');
    document.head.appendChild(style);
  }
}
